import React, { createContext, useContext, useState, ReactNode, useEffect, useRef, useCallback } from "react";
import { Loader2, ExternalLink, FileText, X, AlertCircle, BookOpen, Copy, Check, MessageSquare, Scale, Search, Sparkles, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/lib/supabase";
import JSZip from "jszip";
import * as mammoth from "mammoth";
import { jsPDF } from "jspdf";
import { PdfJsViewer } from "@/components/pdf/PdfJsViewer";
import { useIsMobile } from "@/hooks/use-mobile";

interface DocumentViewerContextType {
    isOpen: boolean;
    docUrl: string | null;
    docTitle: string;
    openDocument: (url: string, title?: string, highlight?: string, targetPage?: number | null, metadata?: any) => void;
    closeDocument: () => void;
    registerActionHandler?: (handler: (prompt: string) => void) => void;
}

const DocumentViewerContext = createContext<DocumentViewerContextType | undefined>(undefined);

function decodeQuotedPrintable(input: string): string {
    return input
        .replace(/=\r?\n/g, "")
        .replace(/=([0-9A-Fa-f]{2})/g, (_, hex) => String.fromCharCode(parseInt(hex, 16)));
}

function extractHtmlFromMht(mhtContent: string): string | null {
    const htmlMatch = mhtContent.match(/<html[^>]*>[\s\S]*?<\/html>/i);
    if (!htmlMatch) return null;

    let html = decodeQuotedPrintable(htmlMatch[0]);

    html = html.replace(/<style[^>]*>[\s\S]*?<\/style>/gi, "");
    html = html.replace(/<xml>[\s\S]*?<\/xml>/gi, "");
    html = html.replace(/<o:[^>]*>[\s\S]*?<\/o:[^>]*>/gi, "");
    html = html.replace(/<v:[^>]*>[\s\S]*?<\/v:[^>]*>/gi, "");
    html = html.replace(/\sstyle\s*=\s*['"][^'"]*['"]/gi, "");

    const bodyMatch = html.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
    return bodyMatch ? bodyMatch[1] : html;
}

export function DocumentViewerProvider({ children }: { children: ReactNode }) {
    const isMobile = useIsMobile();
    const [isOpen, setIsOpen] = useState(false);
    const [docUrl, setDocUrl] = useState<string | null>(null);
    const [docTitle, setDocTitle] = useState<string>("Documento");
    const [highlightText, setHighlightText] = useState<string | null>(null);
    const [targetPage, setTargetPage] = useState<number | null>(null);
    const [docMetadata, setDocMetadata] = useState<any>(null);
    const [copiedCitation, setCopiedCitation] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [viewerError, setViewerError] = useState<string | null>(null);
    const [convertedPdfUrl, setConvertedPdfUrl] = useState<string | null>(null);
    
    const [visorWidth, setVisorWidth] = useState(45); // default 45% width for comfortable reading
    const [isDragging, setIsDragging] = useState(false);
    const actionHandlerRef = useRef<((prompt: string) => void) | null>(null);

    const registerActionHandler = useCallback((handler: (prompt: string) => void) => {
        actionHandlerRef.current = handler;
    }, []);

    useEffect(() => {
        if (!isDragging) return;

        const handlePointerMove = (e: PointerEvent) => {
            const newWidth = ((window.innerWidth - e.clientX) / window.innerWidth) * 100;
            // Constrain visor width between 20% and 80%
            if (newWidth >= 20 && newWidth <= 80) {
                setVisorWidth(newWidth);
            }
        };

        const handlePointerUp = () => {
            setIsDragging(false);
            document.body.style.cursor = 'default';
        };

        document.body.style.cursor = 'col-resize';
        document.addEventListener('pointermove', handlePointerMove);
        document.addEventListener('pointerup', handlePointerUp);

        return () => {
            document.removeEventListener('pointermove', handlePointerMove);
            document.removeEventListener('pointerup', handlePointerUp);
            document.body.style.cursor = 'default';
        };
    }, [isDragging]);

    const openDocument = (url: string, title?: string, highlight?: string, page?: number | null, metadata?: any) => {
        let normalizedUrl = url || '';
        let extractedPage = page ?? null;
        let extractedHighlight = highlight || null;

        // If URL contains hash fragments like #page=X&search=Y, extract them if not already explicitly provided
        if (url && url.includes('#')) {
            const hash = url.substring(url.indexOf('#') + 1);
            const params = new URLSearchParams(hash);
            if (extractedPage === null && params.get('page')) {
                const parsedP = parseInt(params.get('page')!, 10);
                if (!isNaN(parsedP)) extractedPage = parsedP;
            }
            if (!extractedHighlight && params.get('search')) {
                extractedHighlight = decodeURIComponent(params.get('search')!);
            }
        }

        const envUrl = import.meta.env.VITE_SUPABASE_URL;

        if (envUrl && url && url.includes(".supabase.co")) {
            try {
                const targetHost = new URL(envUrl).hostname;
                const currentUrl = new URL(url);
                if (currentUrl.hostname !== targetHost && currentUrl.hostname.includes("supabase.co")) {
                    currentUrl.hostname = targetHost;
                    normalizedUrl = currentUrl.toString();
                }
            } catch (e) {
                console.error("Error normalizing document URL:", e);
            }
        }

        setDocUrl(normalizedUrl);
        setDocTitle(title || "Documento Jurídico");
        setHighlightText(extractedHighlight);
        setTargetPage(extractedPage);
        setDocMetadata(metadata || null);
        setViewerError(null);
        if (convertedPdfUrl) {
            URL.revokeObjectURL(convertedPdfUrl);
            setConvertedPdfUrl(null);
        }
        setIsLoading(true);
        setIsOpen(true);
    };

    useEffect(() => {
        if (!isOpen || !docUrl) return;

        const cleanDocUrl = docUrl ? docUrl.split('?')[0].split('#')[0] : '';
        const isCitation = !docUrl || docUrl.startsWith('citation:') || docUrl.includes('/api/v1/documents/raw/');
        const isDocx = cleanDocUrl.toLowerCase().endsWith('.docx');
        const isPdf = !isCitation && cleanDocUrl.toLowerCase().endsWith('.pdf');

        if (isCitation) {
            setIsLoading(false);
            setViewerError(null);
            return;
        }

        if (!isDocx && !isPdf) {
            setIsLoading(false);
            return;
        }

        let cancelled = false;

        const fetchAndExtract = async () => {
            setIsLoading(true);
            setViewerError(null);

            try {
                const { data: { session } } = await supabase.auth.getSession();
                const headers: Record<string, string> = {};

                if (session?.access_token && (docUrl.includes("supabase.co") || docUrl.includes(window.location.hostname))) {
                    headers["Authorization"] = `Bearer ${session.access_token}`;
                }

                const fetchUrl = docUrl.split('#')[0];
                const response = await fetch(fetchUrl, { headers });
                if (!response.ok) throw new Error(`HTTP ${response.status}: ${response.statusText}`);

                const blob = await response.blob();
                if (blob.size === 0) throw new Error("El archivo está vacío");

                if (cancelled) return;

                if (isPdf) {
                    const pdfBlob = new Blob([blob], { type: "application/pdf" });
                    const pdfUrl = URL.createObjectURL(pdfBlob);
                    if (!cancelled) {
                        setConvertedPdfUrl(pdfUrl);
                        setIsLoading(false);
                    }
                    return;
                }

                const textPreview = await blob.slice(0, 1000).text();
                const isMhtml = textPreview.includes("multipart/related") ||
                    textPreview.includes("mhtDocumentPart") ||
                    textPreview.includes("Content-Type: text/html");

                let htmlContent: string | null = null;

                if (isMhtml) {
                    const fullText = await blob.text();
                    htmlContent = extractHtmlFromMht(fullText);
                } else {
                    const arrayBuffer = await blob.arrayBuffer();
                    try {
                        const zip = new JSZip();
                        await zip.loadAsync(arrayBuffer);
                        
                        const afchunk = zip.file("word/afchunk.mht");
                        if (afchunk) {
                            const mhtContent = await afchunk.async("string");
                            htmlContent = extractHtmlFromMht(mhtContent);
                        } else {
                            const result = await mammoth.convertToHtml({ arrayBuffer });
                            htmlContent = result.value;
                        }
                    } catch (zipErr) {
                        htmlContent = `<p>${await blob.text()}</p>`;
                    }
                }

                if (!htmlContent) throw new Error("No se pudo extraer contenido del archivo");

                if (!cancelled) {
                    const doc = new jsPDF({ unit: 'pt', format: 'letter', orientation: 'portrait' });
                    
                    const printContainer = document.createElement("div");
                    printContainer.style.position = "absolute";
                    printContainer.style.top = "0";
                    printContainer.style.left = "0";
                    printContainer.style.zIndex = "-9999";
                    printContainer.style.width = "800px";
                    printContainer.style.padding = "40px";
                    printContainer.style.background = "white";
                    printContainer.style.color = "black";
                    printContainer.style.fontFamily = "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif";
                    printContainer.innerHTML = htmlContent;
                    document.body.appendChild(printContainer);

                    doc.html(printContainer, {
                        callback: function (pdf) {
                            document.body.removeChild(printContainer);
                            const pdfBlob = pdf.output("blob");
                            const pdfUrl = URL.createObjectURL(pdfBlob);
                            
                            if (!cancelled) {
                                setConvertedPdfUrl(pdfUrl);
                                setIsLoading(false);
                            }
                        },
                        autoPaging: 'text',
                        x: 0,
                        y: 0,
                        width: 612,
                        windowWidth: 800
                    });
                }
            } catch (err: any) {
                if (!cancelled) {
                    // Graceful fallback: If fetch failed, but we have highlightText or excerpt,
                    // do not show an unhandled error screen; allow snippet view to render!
                    if (highlightText) {
                        console.warn("Direct document fetch failed, using snippet fallback:", err);
                        setIsLoading(false);
                        return;
                    }
                    const rawMsg = String(err?.message || "");
                    const isCorsOrNetwork = rawMsg.toLowerCase().includes("load failed") || 
                        rawMsg.toLowerCase().includes("failed to fetch") ||
                        rawMsg.toLowerCase().includes("networkerror");
                    const friendly = isCorsOrNetwork
                        ? "El archivo remoto no pudo cargarse directamente debido a restricciones de seguridad del navegador (CORS) o red."
                        : (rawMsg || "Error al procesar el documento");
                    setViewerError(friendly);
                    setIsLoading(false);
                }
            }
        };

        fetchAndExtract();
        return () => { cancelled = true; };
    }, [isOpen, docUrl, highlightText]);

    const closeDocument = () => {
        setIsOpen(false);
        setTimeout(() => {
            if (convertedPdfUrl) {
                URL.revokeObjectURL(convertedPdfUrl);
                setConvertedPdfUrl(null);
            }
            setDocUrl(null);
            setHighlightText(null);
            setTargetPage(null);
            setDocMetadata(null);
            setViewerError(null);
        }, 300);
    };

    const openInNewTab = () => {
        if (docUrl && !docUrl.startsWith('citation:') && !docUrl.includes('/api/v1/documents/raw/')) {
            window.open(docUrl, '_blank');
        } else if (docTitle) {
            window.open(`https://www.google.com/search?q=${encodeURIComponent(docTitle + " Colombia SUIN Juriscol")}`, '_blank');
        }
    };

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape" && isOpen) {
                closeDocument();
            }
        };
        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [isOpen]);

    return (
        <DocumentViewerContext.Provider value={{ isOpen, docUrl, docTitle, openDocument, closeDocument, registerActionHandler }}>
            <div className="flex h-screen w-full overflow-hidden bg-white">
                <div 
                    className={`${!isDragging ? 'transition-all duration-300 ease-in-out' : ''} h-full overflow-y-auto ${isOpen && !isMobile ? 'border-r border-slate-200 shadow-sm' : ''} z-10 flex-shrink-0 ${isDragging ? 'pointer-events-none select-none' : ''}`}
                    style={{ width: (isOpen && !isMobile) ? `calc(${100 - visorWidth}% - 8px)` : '100%' }}
                >
                    {children}
                </div>

                {isOpen && !isMobile && (
                    <div 
                        className="w-[8px] cursor-col-resize bg-slate-100 hover:bg-blue-500 active:bg-blue-600 border-x border-slate-200 transition-colors z-20 flex-shrink-0 relative group flex items-center justify-center"
                        onPointerDown={(e) => {
                            e.preventDefault();
                            setIsDragging(true);
                        }}
                        onDoubleClick={() => setVisorWidth(45)}
                        title="Doble clic para restablecer el tamaño (45%)"
                    >
                        <div className="absolute inset-y-0 -left-2 -right-2 bg-transparent cursor-col-resize" />
                        <div className="flex flex-col gap-1 py-1.5 px-0.5 rounded bg-slate-200 group-hover:bg-blue-100 group-hover:text-blue-700 transition-colors shadow-sm">
                            <div className="w-1 h-1 rounded-full bg-slate-500" />
                            <div className="w-1 h-1 rounded-full bg-slate-500" />
                            <div className="w-1 h-1 rounded-full bg-slate-500" />
                        </div>
                    </div>
                )}

                <div 
                    className={
                        isMobile
                            ? `fixed inset-0 z-50 bg-white flex flex-col h-full w-full pt-[env(safe-area-inset-top,0px)] pb-[env(safe-area-inset-bottom,0px)] shadow-2xl animate-in slide-in-from-bottom-3 duration-300 ${isOpen ? 'flex' : 'hidden'}`
                            : `${!isDragging ? 'transition-all duration-300 ease-in-out' : ''} h-full bg-slate-50 flex flex-col flex-shrink-0 ${isOpen ? 'translate-x-0' : 'translate-x-full hidden'} ${isDragging ? 'pointer-events-none select-none' : ''}`
                    }
                    style={!isMobile ? { width: isOpen ? `${visorWidth}%` : '0%' } : undefined}
                >
                    {isOpen && docUrl && (() => {
                            const cleanDocUrl = docUrl ? docUrl.split('?')[0].split('#')[0] : '';
                            const isCitation = !docUrl || docUrl.startsWith('citation:') || docUrl.includes('/api/v1/documents/raw/');
                            const isDocx = cleanDocUrl.toLowerCase().endsWith('.docx');
                            const isPdf = !isCitation && cleanDocUrl.toLowerCase().endsWith('.pdf');
                            const displayUrl = convertedPdfUrl || docUrl;

                            // If this is a citation, excerpt, or fallback snippet view
                            if (isCitation || (!convertedPdfUrl && !isLoading && !viewerError && highlightText)) {
                                const handleCopyCitation = () => {
                                    const textToCopy = highlightText || docMetadata?.excerpt || docTitle;
                                    const attribution = `«${textToCopy}»\n— ${docTitle}${docMetadata?.article ? `, ${docMetadata.article}` : ''} (Fuente: SarAI Legal Intelligence)`;
                                    navigator.clipboard.writeText(attribution);
                                    setCopiedCitation(true);
                                    setTimeout(() => setCopiedCitation(false), 2000);
                                };

                                return (
                                    <div className="flex flex-col h-full w-full bg-slate-50 overflow-hidden animate-in fade-in duration-300">
                                        {/* Top Header */}
                                        <div className="bg-white border-b border-slate-200 px-5 py-3.5 flex items-center justify-between shrink-0 shadow-2xs">
                                            <div className="flex items-center gap-3 min-w-0">
                                                <div className="w-9 h-9 rounded-xl bg-blue-50 border border-blue-200 flex items-center justify-center text-blue-600 shrink-0 shadow-2xs">
                                                    <Scale className="h-4.5 w-4.5" />
                                                </div>
                                                <div className="min-w-0">
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-[10px] font-bold uppercase tracking-wider text-blue-700 bg-blue-50 px-2 py-0.5 rounded-full border border-blue-100">
                                                            Fundamento Jurídico
                                                        </span>
                                                        {docMetadata?.isCanonical && (
                                                            <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-100 flex items-center gap-1">
                                                                <ShieldCheck className="h-3 w-3" /> Canónica
                                                            </span>
                                                        )}
                                                    </div>
                                                    <h3 className="text-sm font-bold text-slate-800 truncate mt-0.5" title={docTitle}>
                                                        {docTitle}
                                                    </h3>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-1">
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="h-8 w-8 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg"
                                                    onClick={closeDocument}
                                                    title="Cerrar visor (Esc)"
                                                >
                                                    <X className="h-4.5 w-4.5" />
                                                </Button>
                                            </div>
                                        </div>

                                        {/* Body Content */}
                                        <div className="flex-1 overflow-y-auto p-4 sm:p-5 space-y-4">
                                            {/* Pills row */}
                                            <div className="flex flex-wrap items-center gap-2">
                                                {docMetadata?.article && (
                                                    <span className="text-xs font-semibold px-2.5 py-1 rounded-md bg-white border border-slate-200 text-slate-700 shadow-2xs">
                                                        📜 {docMetadata.article}
                                                    </span>
                                                )}
                                                {targetPage && (
                                                    <span className="text-xs font-semibold px-2.5 py-1 rounded-md bg-white border border-slate-200 text-slate-700 shadow-2xs">
                                                        📄 Página {targetPage}
                                                    </span>
                                                )}
                                                {docMetadata?.vigencia && (
                                                    <span className="text-xs font-semibold px-2.5 py-1 rounded-md bg-emerald-50 border border-emerald-200 text-emerald-700 shadow-2xs">
                                                        ✓ Vigencia: {docMetadata.vigencia}
                                                    </span>
                                                )}
                                            </div>

                                            {/* Quoted Text Card */}
                                            <div className="bg-white rounded-2xl border border-slate-200/90 shadow-2xs p-5 relative overflow-hidden">
                                                <div className="absolute top-0 left-0 bottom-0 w-1.5 bg-gradient-to-b from-blue-500 to-indigo-600" />
                                                <div className="flex items-center justify-between mb-3">
                                                    <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1.5">
                                                        <BookOpen className="h-3.5 w-3.5 text-blue-500" />
                                                        Texto Jurídico Citado por SarAI
                                                    </span>
                                                    <button
                                                        type="button"
                                                        onClick={handleCopyCitation}
                                                        className="text-xs font-medium text-slate-500 hover:text-blue-600 flex items-center gap-1.5 px-2.5 py-1 rounded-md hover:bg-slate-100 transition-colors"
                                                    >
                                                        {copiedCitation ? (
                                                            <>
                                                                <Check className="h-3.5 w-3.5 text-emerald-600" />
                                                                <span className="text-emerald-600 font-semibold">¡Copiado!</span>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <Copy className="h-3.5 w-3.5" />
                                                                <span>Copiar cita</span>
                                                            </>
                                                        )}
                                                    </button>
                                                </div>
                                                <div className="text-slate-800 text-[14px] leading-relaxed whitespace-pre-wrap font-serif italic pl-3 border-l-2 border-blue-200/80 bg-blue-50/20 py-2 rounded-r-lg">
                                                    "{highlightText || docMetadata?.excerpt || "Extracto jurídico referenciado en el dictamen legal."}"
                                                </div>
                                            </div>

                                            {/* Quick Actions Panel */}
                                            <div className="bg-gradient-to-br from-blue-50/70 to-indigo-50/40 rounded-2xl border border-blue-100 p-4 space-y-3">
                                                <div className="flex items-center gap-2">
                                                    <Sparkles className="h-4 w-4 text-blue-600" />
                                                    <span className="text-xs font-bold text-blue-900">Acciones con SarAI Legal</span>
                                                </div>
                                                <p className="text-xs text-blue-700/80 leading-relaxed">
                                                    Puedes solicitarle a SarAI que amplíe la interpretación o explique los efectos procesales de esta norma.
                                                </p>
                                                <div className="flex flex-wrap gap-2 pt-1">
                                                    <Button
                                                        size="sm"
                                                        className="bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold rounded-lg shadow-2xs gap-1.5"
                                                        onClick={() => {
                                                            if (actionHandlerRef.current) {
                                                                const query = `Explica en detalle cómo aplica ${docTitle} ${docMetadata?.article ? `(${docMetadata.article})` : ''} a mi consulta actual y cuáles son las mejores defensas o argumentos.`;
                                                                actionHandlerRef.current(query);
                                                                if (isMobile) closeDocument();
                                                            }
                                                        }}
                                                    >
                                                        <MessageSquare className="h-3.5 w-3.5" />
                                                        Consultar aplicación en el chat
                                                    </Button>
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        className="bg-white hover:bg-slate-50 text-slate-700 border-slate-200 text-xs font-semibold rounded-lg shadow-2xs gap-1.5"
                                                        onClick={openInNewTab}
                                                    >
                                                        <Search className="h-3.5 w-3.5 text-slate-500" />
                                                        Buscar en SUIN / Rama Judicial
                                                    </Button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            }

                            if ((isDocx || isPdf) && viewerError) {
                                const is404 = viewerError.includes("404");
                                const friendlyMessage = is404
                                    ? "El documento no se encuentra disponible o no existe en el servidor (Error 404)."
                                    : viewerError;

                                return (
                                    <div className="relative flex flex-col items-center justify-center p-8 text-center animate-in fade-in zoom-in-95 duration-500 h-full w-full bg-white rounded-2xl border border-slate-200/60 shadow-[0_12px_40px_rgba(0,0,0,0.06)]">
                                        {/* Botón X superior derecho para cerrar el visor */}
                                        <button
                                            onClick={closeDocument}
                                            className="absolute top-4 right-4 p-2 rounded-xl text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors z-30"
                                            title="Cerrar visor"
                                        >
                                            <X className="h-5 w-5" />
                                        </button>

                                        <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mb-4 text-red-600 border border-red-100 shadow-sm">
                                            <AlertCircle className="h-8 w-8" />
                                        </div>
                                        <h4 className="text-base font-bold text-slate-800 mb-1.5">
                                            {is404 ? "Documento no encontrado" : "Error de Previsualización"}
                                        </h4>
                                        <p className="text-xs text-slate-500 mb-6 max-w-[280px] leading-relaxed">
                                            {friendlyMessage}
                                        </p>
                                        <div className="flex items-center gap-2">
                                            <Button
                                                variant="outline"
                                                className="text-xs font-bold rounded-xl px-4 py-2 border-slate-200 hover:bg-slate-50"
                                                onClick={closeDocument}
                                            >
                                                <X className="h-3.5 w-3.5 mr-1.5" />
                                                Cerrar visor
                                            </Button>
                                            <Button
                                                className="bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold rounded-xl px-4 py-2 shadow-lg shadow-blue-500/20"
                                                onClick={openInNewTab}
                                            >
                                                <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
                                                Abrir en nueva pestaña
                                            </Button>
                                        </div>
                                    </div>
                                );
                            }

                            if ((isDocx || isPdf) && !convertedPdfUrl && isLoading) {
                                return (
                                    <div className="h-full w-full flex flex-col items-center justify-center bg-white relative">
                                        <button
                                            onClick={closeDocument}
                                            className="absolute top-3 right-3 p-2 rounded-xl text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors z-30"
                                            title="Cerrar visor"
                                        >
                                            <X className="h-5 w-5" />
                                        </button>
                                        <div className="w-16 h-16 rounded-2xl bg-white border border-slate-100 shadow-xl shadow-slate-200/50 flex items-center justify-center mb-4">
                                            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                                        </div>
                                        <p className="text-sm font-bold text-slate-700">Analizando y preparando documento</p>
                                        <p className="text-xs text-slate-400 mt-1 font-medium animate-pulse">Optimizando visualización de SarAI...</p>
                                    </div>
                                );
                            }

                            return (
                                <PdfJsViewer
                                    key={displayUrl}
                                    url={displayUrl}
                                    title={docTitle}
                                    highlightText={highlightText}
                                    targetPage={targetPage}
                                    onClose={closeDocument}
                                    onOpenInNewTab={openInNewTab}
                                    onDocumentAction={(prompt) => {
                                        if (actionHandlerRef.current) {
                                            actionHandlerRef.current(prompt);
                                        }
                                    }}
                                />
                            );
                        })()
                    }
                </div>
            </div>
        </DocumentViewerContext.Provider>
    );
}

export function useDocumentViewer() {
    const context = useContext(DocumentViewerContext);
    if (context === undefined) {
        throw new Error("useDocumentViewer must be used within a DocumentViewerProvider");
    }
    return context;
}
